import { FC } from 'react'
import CommentIcon from 'shared-svg/CommentHollow.svg'
import { avatarColor, commentsLabel } from '../comment-format'
import CommentHubAvatar from '../CommentHubAvatar'
import { ConversationContext } from './ConversationContext'
import styles from './ConversationEndBlock.module.css'
import { useConversationPreview } from './useConversationPreview'

/** The end block's slot holds two faces, then the "+". */
const END_BLOCK_FACES = 2

/**
 * End-of-article entry to the conversation panel — it replaces the inline thread (the host wraps
 * it in `#comments-section`). Server-rendered with neutral copy: it renders from the host's
 * `containerId` prop (from `PostContext`), never from `ConversationContext.containerId`, which
 * registers only after the host's effect; the count fills in after mount
 * inside a line whose height is reserved, and the avatar stack (up to two recent commenters, then
 * the "+" circle — just the "+" before the preview lands or at zero) sits in a fixed-height slot
 * flush with the card's left edge, so the block never jumps vertically. An empty thread (the preview's total is 0 — never claimed before it is
 * known) invites the first comment and opens the panel with the composer focused.
 */
interface ConversationEndBlockProps {
  /** The article's commentHub container — the host's (`ArticleConversation`). */
  containerId: string
}

const ConversationEndBlock: FC<ConversationEndBlockProps> = ({ containerId }) => {
  const conversation = ConversationContext.useOptionalContainer()
  const preview = useConversationPreview(containerId)

  if (!conversation) {
    return null
  }

  const total = preview.status === 'ready' ? preview.total : null
  let text = 'Read what readers said or add your take.'
  const empty = total === 0
  if (empty) {
    text = 'No one has commented yet. Start the conversation.'
  } else if (total !== null) {
    // The total counts replies too: comments, not readers (Guillaume, 2026-10-24).
    text = `${commentsLabel(total)}. Read what readers said or add your take.`
  }
  const faces = preview.status === 'ready' ? preview.recentAuthors.slice(0, END_BLOCK_FACES) : []

  return (
    <section className={styles.block} aria-label="Join the conversation">
      <div className={styles.lead}>
        <div className={styles.faces} aria-hidden="true">
          {faces.map(author => (
            <CommentHubAvatar
              key={author.id}
              className={styles.face}
              src={author.avatarUrl}
              name={author.name ?? 'Anonymous'}
              color={avatarColor(author.id)}
            />
          ))}
          <span className={styles.plus}>{'+'}</span>
        </div>
        <div className={styles.copy}>
          <h2 className={styles.title}>{'Conversation'}</h2>
          <p className={styles.text}>{text}</p>
        </div>
      </div>
      <button
        type="button"
        className={styles.button}
        onClick={() => conversation.open('end-block', empty ? { focusComposer: true } : undefined)}
      >
        <CommentIcon className={styles.buttonIcon} aria-hidden="true" />
        {empty ? 'Write the first comment' : 'Open conversation'}
      </button>
    </section>
  )
}

export default ConversationEndBlock
